import { useCallback, useEffect, useRef, useState } from "react";
import "./motion.css";

// One motion language for every overlay and screen in the app (it started on
// NewCreativeModal); the keyframes and classes live in motion.css.
//
//   center  a modal: the panel rises 12px and settles from 0.98
//   drawer  a side panel: it slides in 24px from the right
//   menu    a dropdown (`MENU_ENTER_CLASS`): it opens 4px down from its anchor, entry only
//   screen  a route's content (`ScreenEnter`): a short fade and rise on entry, or a fade
//           alone for a page with its own fixed background
export const EASE_OUT = "cubic-bezier(0.16, 1, 0.3, 1)";
export const EASE_IN = "cubic-bezier(0.4, 0, 1, 1)";
export const MODAL_EXIT_MS = 160;
export const SCREEN_ENTER_CLASS = "ah-screen-enter";
// Menus close instantly from many paths (an outside click, a picked item), so a dropdown
// animates only its entry. Set `transformOrigin` to the anchor's corner.
export const MENU_ENTER_CLASS = "ah-menu-enter";

// Open overlays by stacking order, so Escape reaches only the topmost one: a modal opened
// from another (a credit form inside the advertiser settings, a ledger inside the exports
// panel) closes alone. The order is taken during render, which runs parent-first (effects
// run child-first, so registering there would put a nested modal below its parent); a later
// or nested overlay gets a higher number. Each entry says whether that overlay takes Escape
// right now. An overlay stays in the stack, not taking Escape, until it unmounts: while the
// topmost one is not dismissible (a save in flight) or still playing its exit, Escape
// reaches nothing below it.
let nextSequence = 0;
const openOverlays = new Map();
const isTopmost = (sequence) => sequence === Math.max(...openOverlays.keys());

// Motion and dialog wiring for one overlay. Spread `backdropProps` on the full-screen layer
// and `panelProps` on the box (name it with aria-labelledby or aria-label), and call
// `requestClose` wherever the overlay used to call `onClose`: the exit plays, then `onClose`
// runs exactly once (on the panel's own animation end, or a timeout backstop for an engine
// that never reports it).
//   dismissible      Escape (and a backdrop click, with closeOnBackdrop) may close it; pass
//                    false while a save is in flight.
//   closeOnBackdrop  a click on the backdrop itself, not inside the panel, closes it.
// An input inside that handles Escape itself calls e.stopPropagation(): the listener is on
// document.
export function useModalMotion(onClose, { dismissible = true, variant = "center", closeOnBackdrop = false } = {}) {
  const [closing, setClosing] = useState(false);
  const [sequence] = useState(() => ++nextSequence);
  const done = useRef(false);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  const finish = useCallback(() => {
    if (done.current) return;
    done.current = true;
    onCloseRef.current?.();
  }, []);

  useEffect(() => {
    if (!closing) return undefined;
    const timer = setTimeout(finish, MODAL_EXIT_MS + 60);
    return () => clearTimeout(timer);
  }, [closing, finish]);

  const requestClose = useCallback(() => setClosing(true), []);
  const takesEscape = dismissible && !closing;

  useEffect(() => { openOverlays.set(sequence, takesEscape); }, [sequence, takesEscape]);
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && isTopmost(sequence) && openOverlays.get(sequence)) requestClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      openOverlays.delete(sequence);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [sequence, requestClose]);

  const onAnimationEnd = useCallback((e) => {
    if (closing && e.target === e.currentTarget) finish();
  }, [closing, finish]);

  const onBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget && takesEscape) requestClose();
  }, [takesEscape, requestClose]);

  return {
    closing,
    requestClose,
    // ModalShell reads it, so the layout always matches the motion.
    variant,
    backdropProps: {
      className: closing ? "ah-modal-backdrop ah-modal-backdrop--closing" : "ah-modal-backdrop",
      ...(closeOnBackdrop ? { onClick: onBackdropClick } : {}),
    },
    // While closing, the panel is inert (no clicks, no focus): a success path that re-enables
    // its button in a finally must not let a second click submit again during the exit.
    panelProps: {
      role: "dialog",
      "aria-modal": "true",
      ...(closing ? { inert: true } : {}),
      className: [`ah-modal-panel--${variant}`, "ah-modal-panel", closing && "ah-modal-panel--closing"].filter(Boolean).join(" "),
      onAnimationEnd,
    },
  };
}
